import {UserDefinedObjectTemplateAdditionalColumnType as UdoColumnType} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {
    getOptionalOttopilotDispositionValue,
    getOttopilotDispositionValue,
} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotDispositionColumnConfig";
import {
    getOptionalOttopilotOrganizationalUnitValue,
    getOttopilotOrganizationalUnitValue,
} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotOrganizationalUnitColumnConfig";
import type {OttopilotEventContainer} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import type {OttopilotEvent} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEvent";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";

import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";

export function requireEventContainerTemplate(
    templates: ReadonlyArray<EventContainerTemplate>,
    templateId: Uuid,
): EventContainerTemplate {
    const template = templates.find((candidate) => candidate.id === templateId);
    if (template === undefined) {
        throw new Error(`Event Container template not found: ${templateId}`);
    }
    return template;
}

export function requireEventTemplate(
    templates: ReadonlyArray<OttopilotEventTemplate>,
    templateId: Uuid,
): OttopilotEventTemplate {
    const template = templates.find((candidate) => candidate.id === templateId);
    if (template === undefined) {
        throw new Error(`Event template not found: ${templateId}`);
    }
    return template;
}

export function getEventContainerDispositionColumnId(template: EventContainerTemplate): Uuid {
    return template.configuration.dispositionColumnConfig.columnId;
}

export function getEventDispositionColumnId(template: OttopilotEventTemplate): Uuid {
    return template.configuration.dispositionColumnConfig.columnId;
}

export function getEventContainerOrganizationalUnitColumnId(template: EventContainerTemplate): Uuid {
    return template.configuration.organizationalUnitColumnConfig.columnId;
}

export function getEventContainerDispositionOptions(getAdditionalColumns: CapabilityUdoColumnResolver,
    template: EventContainerTemplate,
    optionsByUserDefinedObjectTemplateId: ReadonlyMap<Uuid, ReadonlyArray<{displayName: string; id: Uuid}>>,
): ReadonlyArray<{displayName: string; id: Uuid}> {
    const columnId = getEventContainerDispositionColumnId(template);
    const column = getAdditionalColumns(template).find((candidate) => candidate.id === columnId);
    if (column === undefined || column.type !== UdoColumnType.userDefinedObject) {
        throw new Error(`Event Container disposition column is not a single-value UDO column: ${columnId}`);
    }
    return optionsByUserDefinedObjectTemplateId.get(column.configuration.userDefinedObjectTemplateId) ?? [];
}

export function getEventDispositionOptions(getAdditionalColumns: CapabilityUdoColumnResolver,
    template: OttopilotEventTemplate,
    optionsByUserDefinedObjectTemplateId: ReadonlyMap<Uuid, ReadonlyArray<{displayName: string; id: Uuid}>>,
): ReadonlyArray<{displayName: string; id: Uuid}> {
    const columnId = getEventDispositionColumnId(template);
    const column = getAdditionalColumns(template).find((candidate) => candidate.id === columnId);
    if (column === undefined || column.type !== UdoColumnType.userDefinedObject) {
        throw new Error(`Event disposition column is not a single-value UDO column: ${columnId}`);
    }
    return optionsByUserDefinedObjectTemplateId.get(column.configuration.userDefinedObjectTemplateId) ?? [];
}

export function getEventContainerDispositionId(
    eventContainer: Pick<OttopilotEventContainer, "additionalColumnValues">,
    template: EventContainerTemplate,
): Uuid {
    return getOttopilotDispositionValue(eventContainer, template.configuration.dispositionColumnConfig);
}

export function getOptionalEventContainerDispositionId(
    eventContainer: Pick<OttopilotEventContainer, "additionalColumnValues">,
    template: EventContainerTemplate,
): Uuid | null {
    return getOptionalOttopilotDispositionValue(eventContainer, template.configuration.dispositionColumnConfig);
}

export function getEventDispositionId(
    event: Pick<OttopilotEvent, "additionalColumnValues">,
    template: OttopilotEventTemplate,
): Uuid {
    return getOttopilotDispositionValue(event, template.configuration.dispositionColumnConfig);
}

export function getOptionalEventDispositionId(
    event: Pick<OttopilotEvent, "additionalColumnValues">,
    template: OttopilotEventTemplate,
): Uuid | null {
    return getOptionalOttopilotDispositionValue(event, template.configuration.dispositionColumnConfig);
}

export function getEventContainerOrganizationalUnitId(
    eventContainer: Pick<OttopilotEventContainer, "additionalColumnValues">,
    template: EventContainerTemplate,
): Uuid {
    return getOttopilotOrganizationalUnitValue(eventContainer, template.configuration.organizationalUnitColumnConfig);
}

export function getOptionalEventContainerOrganizationalUnitId(
    eventContainer: Pick<OttopilotEventContainer, "additionalColumnValues">,
    template: EventContainerTemplate,
): Uuid | null {
    return getOptionalOttopilotOrganizationalUnitValue(eventContainer, template.configuration.organizationalUnitColumnConfig);
}
